"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Shield,
  Eye,
  Filter,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Calendar,
  Trash2,
} from "lucide-react";
import { useAuthStore } from "../store/authStore";
import api from "../services/api";
import toast from "react-hot-toast";
import ConsentDetailsModal from "../components/ConsentDetailsModal";
import ConfirmationModal from "../components/ConfirmationModal";

interface UserConsent {
  _id: string;
  userId: string;
  consentId: string;
  partnerId: string;
  partnerName: string;
  consentStatus: "Pending" | "Approved" | "Rejected" | "Expired";
  consentCreationDate: string;
  apiList: string[];
  accessToken?: string;
  refreshToken?: string;
  accessTokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  netBankingUserId?: string;
  documentUploaded: "Y" | "N";
  createdAt: string;
  lastUpdated: string;
}

interface ConsentStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  expired: number;
}

export default function ConsentPage() {
  const { user } = useAuthStore();
  const [consents, setConsents] = useState<UserConsent[]>([]);
  const [stats, setStats] = useState<ConsentStats>({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    expired: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState<UserConsent | null>(
    null
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [consentToDelete, setConsentToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchConsents();
  }, [currentPage, statusFilter]);

  const fetchConsents = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        userId: user?.id || "",
      });

      if (statusFilter !== "all") {
        params.append("consentStatus", statusFilter);
      }

      const response = await api.get(`/user-consents?${params}`);

      if (response.data.success) {
        setConsents(response.data.data);
        setTotalPages(response.data.pagination.total_pages);

        // Calculate stats
        const allConsentsResponse = await api.get(
          `/user-consents/user/${user?.id}`
        );
        if (allConsentsResponse.data.success) {
          const allConsents = allConsentsResponse.data.data;
          setStats({
            total: allConsents.length,
            pending: allConsents.filter(
              (c: UserConsent) => c.consentStatus === "Pending"
            ).length,
            approved: allConsents.filter(
              (c: UserConsent) => c.consentStatus === "Approved"
            ).length,
            rejected: allConsents.filter(
              (c: UserConsent) => c.consentStatus === "Rejected"
            ).length,
            expired: allConsents.filter(
              (c: UserConsent) => c.consentStatus === "Expired"
            ).length,
          });
        }
      }
    } catch (error: any) {
      console.error("Error fetching consents:", error);
      toast.error("Failed to fetch consents");
    } finally {
      setLoading(false);
    }
  };

  const refreshConsentStatus = async (consentId: string) => {
    try {
      setRefreshing(true);
      // Call the consent status API
      const response = await api.post("/consent/getstatus", {
        consentId,
        accessToken: "your-access-token", // You'll need to get this from somewhere
      });

      if (response.data.success) {
        toast.success("Consent status refreshed!");
        fetchConsents();
      }
    } catch (error: any) {
      console.error("Error refreshing consent status:", error);
      toast.error("Failed to refresh consent status");
    } finally {
      setRefreshing(false);
    }
  };

  const deleteConsent = async (consentId: string) => {
    try {
      const response = await api.delete(`/user-consents/${consentId}`);
      if (response.data.success) {
        toast.success("Consent deleted successfully!");
        fetchConsents();
      }
    } catch (error: any) {
      console.error("Error deleting consent:", error);
      toast.error("Failed to delete consent");
    }
  };

  const handleDeleteClick = (consentId: string) => {
    setConsentToDelete(consentId);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    if (consentToDelete) {
      deleteConsent(consentToDelete);
      setConsentToDelete(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "Rejected":
        return <XCircle className="w-5 h-5 text-red-500" />;
      case "Expired":
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "Expired":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const filteredConsents = consents.filter(
    (consent) =>
      consent.partnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consent.consentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      consent.partnerId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Consent Management
          </h1>
          <p className="text-gray-600">
            Manage your banking API consents and permissions
          </p>
        </div>
        <div className="flex space-x-3 mt-4 sm:mt-0">
          <button
            onClick={fetchConsents}
            disabled={refreshing}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              size={20}
              className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {[
          {
            label: "Total",
            value: stats.total,
            color: "bg-blue-500",
            icon: Shield,
          },
          {
            label: "Pending",
            value: stats.pending,
            color: "bg-yellow-500",
            icon: Clock,
          },
          {
            label: "Approved",
            value: stats.approved,
            color: "bg-green-500",
            icon: CheckCircle,
          },
          {
            label: "Rejected",
            value: stats.rejected,
            color: "bg-red-500",
            icon: XCircle,
          },
          {
            label: "Expired",
            value: stats.expired,
            color: "bg-orange-500",
            icon: AlertTriangle,
          },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {stat.label}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by partner name, consent ID, or partner ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="Expired">Expired</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Consents Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consent Details
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Partner
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  APIs
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredConsents.map((consent, index) => (
                <motion.tr
                  key={consent._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        ID: {consent.consentId}
                      </p>
                      <p className="text-sm text-gray-500">
                        Doc:{" "}
                        {consent.documentUploaded === "Y"
                          ? "Uploaded"
                          : "Not Uploaded"}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {consent.partnerName}
                      </p>
                      <p className="text-sm text-gray-500">
                        ID: {consent.partnerId}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {getStatusIcon(consent.consentStatus)}
                      <span
                        className={`ml-2 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                          consent.consentStatus
                        )}`}
                      >
                        {consent.consentStatus}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {consent.apiList.slice(0, 2).map((api, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                        >
                          {api}
                        </span>
                      ))}
                      {consent.apiList.length > 2 && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          +{consent.apiList.length - 2} more
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar size={16} className="mr-1" />
                      {new Date(consent.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedConsent(consent);
                          setShowDetailsModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => refreshConsentStatus(consent.consentId)}
                        disabled={refreshing}
                        className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh Status"
                      >
                        <RefreshCw
                          size={16}
                          className={refreshing ? "animate-spin" : ""}
                        />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(consent._id)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title="Delete Consent"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredConsents.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No consents found</p>
            <p className="text-gray-400">
              Create your first consent to get started
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Modals */}
      {showDetailsModal && selectedConsent && (
        <ConsentDetailsModal
          consent={selectedConsent}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedConsent(null);
          }}
          onRefresh={() => {
            fetchConsents();
            refreshConsentStatus(selectedConsent.consentId);
          }}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setConsentToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Consent"
        message="Are you sure you want to delete this consent? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}
